import request from '../request'

/**
 * AI · 第三方模型来源管理接口。
 * 一条记录 = 一个第三方 API 来源（来源名 + 接口地址 + 密钥 + 包含的模型清单）。
 */

/**
 * 获取全部第三方模型来源（按上传时间倒序）。
 * @returns {Promise<{list: Array, total: number}>}
 */
export function fetchThirdPartyModelList() {
  return request({
    url: '/api/third-party-model/list',
    method: 'GET',
  })
}

/**
 * 新建一个第三方模型来源。
 * @param {{source: string, url: string, key: string}} payload 表单数据。
 * @returns {Promise<Object>} 新建后的记录
 */
export function createThirdPartyModel(payload) {
  return request({
    url: '/api/third-party-model',
    method: 'POST',
    data: payload,
  })
}

/**
 * 编辑一个第三方模型来源。
 * @param {number|string} id 记录 ID。
 * @param {{source: string, url: string, key: string}} payload 表单数据。
 * @returns {Promise<Object>} 更新后的记录
 */
export function updateThirdPartyModel(id, payload) {
  return request({
    url: `/api/third-party-model/${id}`,
    method: 'PUT',
    data: payload,
  })
}

/**
 * 删除一个第三方模型来源。
 * @param {number|string} id 记录 ID。
 * @returns {Promise<Object>}
 */
export function deleteThirdPartyModel(id) {
  return request({
    url: `/api/third-party-model/${id}`,
    method: 'DELETE',
  })
}

/**
 * 测试单个来源是否仍然可用，同时刷新它包含的模型清单。
 * @param {number|string} id 记录 ID。
 * @returns {Promise<Object>} 测试后的记录
 */
export function testThirdPartyModel(id) {
  return request({
    url: `/api/third-party-model/${id}/test`,
    method: 'POST',
    // 单个来源测试需要等待第三方站点响应，超时时间放宽到 30 秒。
    timeout: 30000,
  })
}

/**
 * 批量测试。不传 ids 表示测试全部来源。
 * @param {Array<number|string>} [ids] 需要测试的记录 ID 列表。
 * @returns {Promise<{total: number, success: number, failed: number, list: Array}>}
 */
export function batchTestThirdPartyModels(ids) {
  return request({
    url: '/api/third-party-model/batch-test',
    method: 'POST',
    data: Array.isArray(ids) && ids.length ? { ids } : {},
    // 批量串行探测，给足时间（每个来源最多 12 秒）。
    timeout: 300000,
  })
}

/**
 * 查看某个来源包含的全部模型。
 * @param {number|string} id 记录 ID。
 * @param {boolean} [refresh=false] 是否强制实时刷新（忽略本地缓存清单）。
 * @returns {Promise<{id: number, source: string, models: string[], modelCount: number, message: string}>}
 */
export function fetchThirdPartyModelModels(id, refresh = false) {
  return request({
    url: `/api/third-party-model/${id}/models`,
    method: 'GET',
    params: refresh ? { refresh: 1 } : undefined,
    timeout: 30000,
  })
}

/**
 * 通过「来源名」查询该来源包含的全部模型。
 * 模型测试页选完来源后调用，本地没有清单时会实时拉取一次。
 * @param {string} source 来源名称（第三方模型里的 source）。
 * @param {boolean} [refresh=false] 是否强制实时刷新（忽略本地缓存清单）。
 * @returns {Promise<{id: number, source: string, models: string[], modelCount: number, message: string}>}
 */
export function fetchThirdPartyModelModelsBySource(source, refresh = false) {
  return request({
    url: '/api/third-party-model/models',
    method: 'GET',
    params: refresh ? { source, refresh: 1 } : { source },
    timeout: 30000,
  })
}

/**
 * 拼接对话补全接口地址（走本服务端代理，避免浏览器跨域被拦截）。
 * @returns {string} 对话补全代理地址。
 */
export function buildThirdPartyChatUrl() {
  return '/api/third-party-model/chat'
}

/**
 * 测试某个「来源名」是否可用，并刷新它包含的模型清单。
 * @param {string} source 来源名称（第三方模型里的 source）。
 * @returns {Promise<Object>} 测试后的记录
 */
export function testThirdPartyModelBySource(source) {
  return request({
    url: '/api/third-party-model/test-by-source',
    method: 'POST',
    data: { source },
    // 来源测试需要等待第三方站点响应，超时时间放宽到 30 秒。
    timeout: 30000,
  })
}

/**
 * 查询某个来源的余额（实时）。
 * 后端走完整登录链路后返回结构化数据，并写入缓存。
 * @param {number|string} id 记录 ID。
 * @returns {Promise<{id: number, source: string, ok: boolean, summary: string, message: string, updatedAt: string, data: Object}>}
 */
export function fetchThirdPartyModelBalance(id) {
  return request({
    url: `/api/third-party-model/${id}/balance`,
    method: 'POST',
    // 余额查询要先走一遍登录 + 授权跳转链，给足 90 秒。
    timeout: 90000,
  })
}

/**
 * 余额总览（读缓存，不触发任何外部请求）。
 * 进页面就调，用于快速渲染上方各来源的余额方块。
 * @returns {Promise<{list: Array, total: number, supportedCount: number}>}
 */
export function fetchBalanceOverview() {
  return request({
    url: '/api/third-party-model/balance/overview',
    method: 'GET',
  })
}

/**
 * 余额总览（实时刷新全部已接入来源）。
 * 后端串行查询，耗时较长，超时放宽到 5 分钟。
 * @returns {Promise<{list: Array, total: number, successCount: number, failedCount: number}>}
 */
export function refreshBalanceOverview() {
  return request({
    url: '/api/third-party-model/balance/overview/refresh',
    method: 'POST',
    timeout: 300000,
  })
}

/**
 * 移动端免登录列表：只返回来源 / 是否可用 / URL / Key / 模型清单。
 * 供手机端（含未登录扫码）只读展示，不依赖登录态。
 * @returns {Promise<{list: Array, total: number}>}
 */
export function fetchMobileThirdPartyModelList() {
  return request({
    url: '/api/third-party-model/mobile/list',
    method: 'GET',
  })
}

/**
 * 移动端免登录详情：URL / Key / 全部模型。
 * @param {number|string} id 记录 ID。
 * @returns {Promise<Object>}
 */
export function fetchMobileThirdPartyModelDetail(id) {
  return request({
    url: `/api/third-party-model/mobile/detail/${id}`,
    method: 'GET',
  })
}
